import { type OrderDetails, CONVEYANCE_TYPES } from '@koala/sdk';
import { FavoriteIcon } from '@/components/account/favorites/favoriteIcon';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledFavoriteOrderCTA } from '@/components/orderDetail/location/header/styles';
import {
  StyledBasketItem,
  StyledBasketItemContainer,
  StyledDate,
  StyledFavoriteCard,
  StyledInlineContent,
  StyledOrderTotal,
  StyledSafeLink,
  StyledTitleAndHeartIconContainer,
} from '@/components/uielements/favoriteCard/styles';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { DATE_FORMAT } from '@/constants/dates';
import { API_FOR_CONVEYANCE_TYPES_SWAP } from '@/constants/global';
import { useDispatch } from '@/redux';
import { toDollars } from '@/utils/basket';
import { formatDate } from '@/utils/dates';
import meActions from '@/redux/me/actions';

interface Props {
  order: OrderDetails;
  index: number;
  reorderBasketId: string;
  reorderBasket: (order: OrderDetails) => void;
  showSubtotal?: boolean;
  showReceiptLink?: boolean;
}

export const FavoriteOrderCard = (props: Props) => {
  const { order, index, reorderBasketId, showSubtotal = false, showReceiptLink = false } = props;
  const dispatch = useDispatch();

  const orderConveyance =
    API_FOR_CONVEYANCE_TYPES_SWAP[order.order_data.basket.conveyance_type.type] ||
    CONVEYANCE_TYPES.PICKUP;

  const hydrateAndToggleReorderModal = (orderDetails: OrderDetails): void => {
    dispatch(meActions.fetchMeOrderDetailsSuccess(orderDetails));
    dispatch(meActions.toggleReorderReceipt(true));
  };

  return (
    <StyledFavoriteCard key={`${order.id}-${index}`}>
      {/* Order details and favorite button*/}
      <StyledTitleAndHeartIconContainer>
        <h3>{formatDate(order.order_data.order.ordered_at, DATE_FORMAT.WRITTEN_OUT_DATE)}</h3>
        <StyledFavoriteOrderCTA>
          <FavoriteIcon order={order} />
        </StyledFavoriteOrderCTA>
      </StyledTitleAndHeartIconContainer>
      <StyledDate>
        {orderConveyance} ordered from {order.store_location?.label}
      </StyledDate>

      {/* Basket Items*/}
      <StyledBasketItemContainer>
        {order.order_data.basket.basket_items.map((basketItem, idx) => (
          <StyledBasketItem key={`${basketItem.id} - ${idx}`}>
            {basketItem.product.name}
            {basketItem.quantity > 1 && ` x${basketItem.quantity}`}
            {/* Add comma if not last item in the list */}
            {idx !== order.order_data.basket.basket_items.length - 1 && ', '}
          </StyledBasketItem>
        ))}
      </StyledBasketItemContainer>

      <StyledInlineContent>
        {/* Subtotal */}
        {showSubtotal && (
          <StyledOrderTotal>Order Total: ${toDollars(order.total)}</StyledOrderTotal>
        )}
        {/* View Receipt link */}
        {showReceiptLink && (
          <StyledSafeLink
            href={`/account/order-detail/${order.order_data.order.order_number}`}
            passHref={true}
            aria-label={`View Order #${order.order_data.order.order_number}`}
          >
            <StringAccessor accessor="order_history.view_reciept_button" />
          </StyledSafeLink>
        )}
        {/* Reorder Button */}
        <StyledPrimaryButton
          data-css-override="StyledPrimaryButton"
          $submitting={reorderBasketId === order.id}
          onClick={() => hydrateAndToggleReorderModal(order)}
          aria-label={`Reorder Order #${order.order_data.order.order_number}`}
        >
          <StringAccessor accessor="order_history.reorder_button" />
        </StyledPrimaryButton>
      </StyledInlineContent>
    </StyledFavoriteCard>
  );
};
